/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */

// JWKS cache utility for template
import { createLocalJWKSet } from "jose";

const DEFAULT_TTL_S = Number(import.meta.env.AUTH0_CACHE_DURATION_S ?? 300);
const STORAGE_KEY = (domain: string) => `jwks:${domain}`;

const inMemoryCache = new Map<string, ReturnType<typeof createLocalJWKSet>>();
let inFlightFetches = new Map<
  string,
  Promise<ReturnType<typeof createLocalJWKSet>>
>();

async function fetchJwksJson(domain: string) {
  const resp = await fetch(`https://${domain}/.well-known/jwks.json`, {
    headers: { Accept: "application/json, application/jwk-set+json" },
  });

  if (!resp.ok) throw new Error(`Failed to fetch jwks.json: ${resp.status}`);

  return (await resp.json()) as any;
}

/**
 * Returns a `jose` `JWKSet` verifier for the given Auth0 domain.
 *
 * Uses a two-level cache to minimise network round-trips:
 * 1. **In-memory** — survives within the same page session.
 * 2. **`sessionStorage`** — survives soft navigations; entries expire after
 *    `AUTH0_CACHE_DURATION_S` seconds (default: 300).
 *
 * Concurrent calls for the same domain are de-duplicated via a single
 * in-flight promise map so the JWKS endpoint is never hit twice at once.
 *
 * @param domain - Auth0 tenant domain (e.g. `\"fufuni.eu.auth0.com\"`).
 * @returns A `jose` local JWK set ready to be passed to `jwtVerify`.
 */
export async function getLocalJwkSet(domain: string) {
  if (inMemoryCache.has(domain)) return inMemoryCache.get(domain)!;
  if (inFlightFetches.has(domain)) return await inFlightFetches.get(domain)!;

  const promise = (async () => {
    try {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY(domain));

        if (raw) {
          const parsed = JSON.parse(raw);
          const ageS = (Date.now() - (parsed.uat || 0)) / 1000;
          const ttl = Number(
            import.meta.env.AUTH0_CACHE_DURATION_S ?? DEFAULT_TTL_S,
          );

          if (ageS < ttl && parsed.jwks) {
            const local = createLocalJWKSet(parsed.jwks);

            inMemoryCache.set(domain, local);

            return local;
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          "Failed to load JWKS from sessionStorage, fetching anew",
          e,
        );
        // ignore
      }

      const jwks = await fetchJwksJson(domain);
      const local = createLocalJWKSet(jwks);

      try {
        sessionStorage.setItem(
          STORAGE_KEY(domain),
          JSON.stringify({ jwks, uat: Date.now() }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Failed to save JWKS to sessionStorage", e);
      }

      inMemoryCache.set(domain, local);

      return local;
    } finally {
      inFlightFetches.delete(domain);
    }
  })();

  inFlightFetches.set(domain, promise);

  return await promise;
}
